import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { PreviewPanel } from './PreviewPanel';
import { chatApi } from '@/lib/chat-api';
import { ChatMessage, JobData } from '@/types/chat';
import { ArrowLeft, Save, RotateCcw } from 'lucide-react';

interface ChatContainerProps {
  sessionId?: string;
  initialJobData?: JobData;
  onSwitchToForm?: (jobData: JobData, sessionId: string | null) => void;
}

export function ChatContainer({ sessionId: initialSessionId, initialJobData, onSwitchToForm }: ChatContainerProps) {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId || null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [jobData, setJobData] = useState<JobData | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedMessage, setFailedMessage] = useState<string | null>(null);

  // Initialize session
  useEffect(() => {
    const initSession = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        if (initialSessionId) {
          // Load existing session
          const response = await chatApi.getMessages(initialSessionId);
          setMessages(response.messages);
          setJobData(response.jobData);
        } else {
          // Create new session
          const response = await chatApi.createSession(initialJobData as Record<string, unknown> | undefined);
          setSessionId(response.sessionId);
          
          // Pre-fill preview panel with initial data if provided
          if (initialJobData) {
            setJobData(initialJobData);
          }
          
          // Add greeting message
          const greetingObj = typeof response.greeting === 'string'
            ? { id: '1', content: response.greeting, timestamp: new Date().toISOString() }
            : response.greeting;
          
          setMessages([{
            id: greetingObj.id,
            role: 'assistant',
            content: greetingObj.content,
            timestamp: greetingObj.timestamp,
          }]);
        }
      } catch (err: any) {
        console.error('Failed to initialize session:', err);
        setError(err.message || 'Erro ao iniciar chat');
        toast.error(err.message || 'Erro ao iniciar chat');
      } finally {
        setIsLoading(false);
      }
    };

    initSession();
  }, [initialSessionId]);

  const handleSendMessage = useCallback(async (content: string) => {
    if (!sessionId) return;

    // Clear any previous error state
    setError(null);
    setFailedMessage(null);

    // Add user message optimistically
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      setIsLoading(true);
      const response = await chatApi.sendMessage(sessionId, content);

      // Add AI response
      const aiMessage: ChatMessage = {
        id: response.messageId,
        role: 'assistant',
        content: response.content,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, aiMessage]);

      // Merge job data incrementally (don't lose existing fields)
      if (response.jobData) {
        setJobData((prev) => {
          if (!prev) return response.jobData;
          return {
            ...prev,
            ...Object.fromEntries(
              Object.entries(response.jobData!).filter(([_, v]) => v !== undefined && v !== null && v !== '')
            ),
          };
        });
      }
    } catch (err: any) {
      console.error('Failed to send message:', err);
      const errorMsg = err.message || 'Erro ao enviar mensagem';
      setError(errorMsg);
      setFailedMessage(content);
      toast.error(errorMsg);
      // Remove optimistic user message on error
      setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  const handleRetry = useCallback(() => {
    if (failedMessage) {
      handleSendMessage(failedMessage);
    }
  }, [failedMessage, handleSendMessage]);

  const handleDismissError = useCallback(() => {
    setError(null);
    setFailedMessage(null);
  }, []);

  const handleSaveJob = async () => {
    if (!sessionId) return;

    try {
      setIsSaving(true);
      setError(null);
      const response = await chatApi.saveJob(sessionId);
      toast.success('Vaga criada com sucesso!');
      navigate(`/jobs/${response.jobId}`);
    } catch (err: any) {
      console.error('Failed to save job:', err);
      const errorMsg = err.message || 'Erro ao salvar vaga';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const isJobComplete = jobData?.title && jobData?.description && 
    jobData?.requirements && jobData.requirements.length > 0 &&
    jobData?.evaluationCriteria && jobData.evaluationCriteria.length > 0;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate('/jobs/create')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-lg font-semibold">Chat IA - Criar Vaga</h2>
        </div>
        <div className="flex gap-2">
          {onSwitchToForm && jobData && (
            <Button variant="outline" onClick={() => onSwitchToForm(jobData, sessionId)}>
              Continuar no Formulário
            </Button>
          )}
          <Button
            onClick={handleSaveJob}
            disabled={!isJobComplete || isSaving}
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Salvando...' : 'Salvar Vaga'}
          </Button>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chat Area (60%) */}
        <div className="w-3/5 flex flex-col border-r">
          <MessageList messages={messages} isStreaming={isLoading} />

          {/* Error banner with retry */}
          {error && (
            <div className="px-4 py-3 bg-red-50 border-t border-red-200 flex items-center justify-between gap-3">
              <p className="text-sm text-red-700 flex-1">{error}</p>
              <div className="flex gap-2 shrink-0">
                {failedMessage && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetry}
                    disabled={isLoading}
                    className="text-red-700 border-red-300 hover:bg-red-100"
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                    Tentar novamente
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismissError}
                  className="text-red-500 hover:text-red-700"
                >
                  Fechar
                </Button>
              </div>
            </div>
          )}

          <MessageInput
            onSend={handleSendMessage}
            disabled={isLoading}
            placeholder="Digite sua mensagem... (Enter para enviar, Shift+Enter para nova linha)"
          />
        </div>

        {/* Preview Panel (40%) */}
        <div className="w-2/5">
          <PreviewPanel jobData={jobData} />
        </div>
      </div>
    </div>
  );
}
